// Pure volume-sizing layer for ORACLE. No side effects, no external calls.

const BASE_FACTORS = {
  BUY_NOW_SAFETY: 1.0,
  BUY_NOW_PRICE:  1.5,
  BUY_TRANCHE:    0.6,
  HOLD:           0.3,
  WAIT:           0.0,
};

function classifyAction(action, safetyUrgency) {
  if (action === 'BUY_NOW') {
    return safetyUrgency === 'critical' ? 'BUY_NOW_SAFETY' : 'BUY_NOW_PRICE';
  }
  return action;
}

// Where does today's spot sit within the full scenario price range?
// Peace-low to Strike-high = the Bayesian "opportunity window".
function computePricePercentile(spot, scenarios) {
  const allLows  = Object.values(scenarios.ppRanges).map(r => r.low);
  const allHighs = Object.values(scenarios.ppRanges).map(r => r.high);
  const minPrice = Math.min(...allLows);
  const maxPrice = Math.max(...allHighs);
  if (maxPrice === minPrice) return 50;
  const pct = ((spot - minPrice) / (maxPrice - minPrice)) * 100;
  return Math.max(0, Math.min(100, pct));
}

export function computeVolumeSizing({
  action,
  safetyUrgency,
  ppSpot,
  scenarios,
  kapsStockMT,
  kapsTransitMT,
  kapsDailyDemandMT,
}) {
  const ev       = scenarios.pp_expected_7d;
  const delta    = Math.round(ev - ppSpot);
  const deltaPct = ppSpot > 0 ? (ev - ppSpot) / ppSpot * 100 : 0;
  const variant    = classifyAction(action, safetyUrgency);
  const baseFactor = BASE_FACTORS[variant] ?? 0;

  const monthlyDemandMT = Math.round((kapsDailyDemandMT || 0) * 30);

  // Price percentile adjustment — only for price-driven BUY actions
  const pricePercentile = computePricePercentile(ppSpot, scenarios);
  let percentileMultiplier = 1.0;
  if (variant === 'BUY_NOW_PRICE' || variant === 'BUY_TRANCHE') {
    if (pricePercentile <= 20)      percentileMultiplier = 1.2;
    else if (pricePercentile >= 80) percentileMultiplier = 0.7;
  }

  // Inventory overlay — trim if already well-stocked
  const coverageDays = kapsDailyDemandMT > 0
    ? Math.round((kapsStockMT + kapsTransitMT) / kapsDailyDemandMT)
    : 0;
  let inventoryMultiplier = 1.0;
  let inventoryNote = null;
  if (coverageDays > 90) {
    inventoryMultiplier = 0.5;
    inventoryNote = 'high';
  } else if (coverageDays > 60) {
    inventoryMultiplier = 0.7;
    inventoryNote = 'elevated';
  }

  const finalFactor = baseFactor * percentileMultiplier * inventoryMultiplier;
  const volumeMT    = Math.round(finalFactor * monthlyDemandMT);
  const valueCNY    = Math.round(volumeMT * ppSpot);

  return {
    volumeMT,
    valueCNY,
    monthlyDemandMT,
    coverageDays,
    pricePercentile: Math.round(pricePercentile),
    variant,
    baseFactor,
    percentileMultiplier,
    inventoryMultiplier,
    finalFactor,
    inventoryNote,
    ppSpot,
    ev,
    delta,
    deltaPct,
  };
}
